import { reasonLabel } from "./data/reasons";
import type { CodeCaseMode, CodeCaseQuery, CodeCaseRow } from "./types";

// CSV export for the code-cases blade (HUB-810 Task 8). RFC 4180 quoting with
// an Excel-friendly UTF-8 BOM and CRLF line endings.

export type CsvValue = string | number | boolean | null | undefined;

// Cells that a spreadsheet would evaluate. A DCN or a reason id never starts
// with these, but the guard is unconditional: a CSV that opens in Excel is a
// CSV that runs whatever a stray "=" says.
const FORMULA = /^[=+\-@\t\r]/;

export function csvCell(v: CsvValue): string {
  if (v === null || v === undefined) return "";
  let s = typeof v === "string" ? v : String(v);
  if (FORMULA.test(s)) s = "'" + s;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** RFC 4180 with a UTF-8 BOM so Excel reads it as UTF-8, and CRLF. */
export function toCsv(header: string[], rows: CsvValue[][]): string {
  const lines = [header, ...rows].map((r) => r.map(csvCell).join(","));
  return "﻿" + lines.join("\r\n");
}

const ERRORED = ["dcn", "episode_id", "batch_id", "received_at", "review_submitted_at", "field", "code", "side_wrong", "verdict", "reasons", "reason_labels"];
const CORPUS = ["dcn", "episode_id", "batch_id", "received_at", "field", "code", "matched"];

/** No patient context, no free-text notes, no reviewer ids — by construction,
 *  not by filtering: the row type carries none of them. */
export function codeCasesCsv(rows: CodeCaseRow[], mode: CodeCaseMode): string {
  if (mode === "corpus") {
    return toCsv(CORPUS, rows.map((r) => [r.id, r.episodeId, r.batchId, r.receivedAt, r.field, r.code, r.matched]));
  }
  return toCsv(ERRORED, rows.map((r) => [
    r.id, r.episodeId, r.batchId, r.receivedAt, r.reviewSubmittedAt, r.field, r.code, r.side, r.verdict,
    r.reasons.join("; "), r.reasons.map(reasonLabel).join("; "),
  ]));
}

export function codeCasesFilename(q: CodeCaseQuery, now: Date = new Date()): string {
  const safe = (s: string) => s.replace(/[^A-Za-z0-9_.-]+/g, "_");
  const what = q.code ? `${safe(q.field ?? "")}-${safe(q.code)}` : safe(q.reason ?? "all");
  return `code-cases-${q.mode}-${what}-${now.toISOString().slice(0, 10)}.csv`;
}

/** Blob + transient anchor. A no-op where object URLs do not exist (jsdom). */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof URL.createObjectURL !== "function") return;
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
